import { type ReactNode } from 'react';
import { Info, AlertTriangle, X } from 'lucide-react';
import { cn } from '@shared/lib/cn';

/**
 * Banner — full-width informational strip. Used for offline notice,
 * payment-due reminder, license-expiry warning, etc. Hairline top + bottom,
 * tinted background, optional dismiss.
 */

interface BannerProps {
  variant?: 'info' | 'warning' | 'danger' | 'success';
  title?: string;
  description?: string;
  children?: ReactNode;
  action?: ReactNode;
  onDismiss?: () => void;
  className?: string;
}

const VARIANT_STYLES: Record<NonNullable<BannerProps['variant']>, string> = {
  info: 'bg-primary-50 text-ink',
  warning: 'bg-[color:color-mix(in_oklch,var(--warning)_18%,var(--surface-paper))] text-ink',
  danger: 'bg-[color:color-mix(in_oklch,var(--danger)_18%,var(--surface-paper))] text-ink',
  success: 'bg-[color:color-mix(in_oklch,var(--success)_18%,var(--surface-paper))] text-ink',
};

const ICON: Record<NonNullable<BannerProps['variant']>, JSX.Element> = {
  info: <Info className="h-4 w-4 text-primary-700" strokeWidth={1.75} />,
  warning: <AlertTriangle className="h-4 w-4 text-warning" strokeWidth={1.75} />,
  danger: <AlertTriangle className="h-4 w-4 text-danger" strokeWidth={2} />,
  success: <Info className="h-4 w-4 text-success" strokeWidth={1.75} />,
};

export function Banner({
  variant = 'info',
  title,
  description,
  children,
  action,
  onDismiss,
  className,
}: BannerProps): JSX.Element {
  const body = title ?? children;

  return (
    <div
      role={variant === 'danger' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-3 px-6 py-3 hairline-t hairline-b',
        VARIANT_STYLES[variant],
        className,
      )}
    >
      <div className="mt-1">{ICON[variant]}</div>
      <div className="flex-1 min-w-0">
        {body ? <p className="text-body font-semibold">{body}</p> : null}
        {description ? <p className="text-body-sm text-ink-secondary mt-0.5">{description}</p> : null}
      </div>
      {action}
      {onDismiss ? (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          className="text-ink-muted hover:text-ink-secondary transition-colors"
        >
          <X className="h-4 w-4" strokeWidth={1.75} />
        </button>
      ) : null}
    </div>
  );
}
